import dayjs from 'dayjs';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { Box, Divider, Grid, IconButton, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/system';

import SbButton from '@Components/Button';
import Modal from '@Components/Modal';

import { getCurrencySymbolByName } from '@Constants/countriesWithCurrency';
import { ShipmentObj } from '@Reducers/Invoice/Type';
import { getInvoiceList } from '@Reducers/InvoiceList';
import { getInvoiceSettings } from '@Reducers/OrganizationSetup';
import { sendAccountDataTo365 } from '@Reducers/d365';
import { AppDispatch, RootStateType } from '@Store';

import { useAiValidation } from '../../../../hooks/useAiValidation';
import { formatNumber, useLocale } from '../../../../utils/formatNumber';
import { AccountHolderUnbilledData, FilterObj } from '../../Type';
import {
  FilterType,
  InvConfig,
  SelectedParamType,
  calculateSelectedPricingSum,
  getIsWyngsMy,
  initialDateRange,
  initialInvConfig,
  isSameAccountEntry,
  updateSelectedAccountRowsList,
  validateInvoiceConfig,
} from '../utils/invoiceAccountUtils';
import InvoiceConfigurationSection from './InvoiceConfigurationSection';
import ShipmentDetailsSection from './ShipmentDetailsSection';

export interface CreateInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  activeSystemList: { display: string; value: string }[];
  isSubmitting: boolean;
  onSubmitInvoice: (config: InvConfig, accountRows: SelectedParamType[], onSuccess?: () => void) => void;
  initialFilterSelectValue: string;
  initialFilterText: string;
  initialDateRange: { startDate: Date; endDate: Date };
  initialSystem: string[];
  initialShipmentType: string[];
  onRestorePageFilters: () => void;
}

const CreateInvoiceModal = React.memo(function CreateInvoiceModal(props: CreateInvoiceModalProps) {
  const {
    open,
    onClose,
    activeSystemList,
    isSubmitting,
    onSubmitInvoice,
    initialFilterSelectValue,
    initialFilterText,
    initialDateRange: pageDateRange,
    initialSystem,
    initialShipmentType,
    onRestorePageFilters,
  } = props;

  const theme = useTheme();
  const locale = useLocale();
  const dispatch = useDispatch<AppDispatch>();
  const isWyngsMy = useMemo(() => getIsWyngsMy(), []);

  const {
    unBilledList: { list: rawList = [], currentPage = 1, perPage = 10, totalCount = 0 },
    loading: listLoading,
  } = useSelector((state: RootStateType) => state.InvoiceList);
  const list = rawList as unknown as AccountHolderUnbilledData[];

  const {
    invoiceSetting,
    loading: orgLoading,
    organizationPublicData,
  } = useSelector((state: RootStateType) => state?.orgSetup);
  const { loading: loadingD365 } = useSelector((state: RootStateType) => state?.D365);
  const { name = '' } = organizationPublicData || {};

  const [modalFilterSelectValue, setModalFilterSelectValue] = useState(initialFilterSelectValue);
  const [modalFilterText, setModalFilterText] = useState(initialFilterText);
  const [modalDateRange, setModalDateRange] = useState(pageDateRange);
  const [modalSystem, setModalSystem] = useState<string[]>(initialSystem);
  const [modalShipmentType, setModalShipmentType] = useState<string[]>(initialShipmentType);
  const [hasModifiedModalFilters, setHasModifiedModalFilters] = useState(false);

  const [modalSelectedAccountRows, setModalSelectedAccountRows] = useState<SelectedParamType[]>([]);
  const [expandedAccounts, setExpandedAccounts] = useState<Record<string, boolean>>({});
  const [modalInvConfig, setModalInvConfig] = useState<InvConfig>(initialInvConfig);

  const { validationStates, validate, clearValidation } = useAiValidation();
  const wasOpenRef = useRef(false);

  // Reset modal-specific state cleanly only when the modal transitions from closed to open.
  // All expandable rows start collapsed by default ({}).
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setModalFilterSelectValue(initialFilterSelectValue);
      setModalFilterText(initialFilterText);
      setModalDateRange(pageDateRange);
      setModalSystem(initialSystem);
      setModalShipmentType(initialShipmentType);
      setHasModifiedModalFilters(false);
      setModalSelectedAccountRows([]);
      setExpandedAccounts({});
      setModalInvConfig(initialInvConfig);
      clearValidation('taxno');
    }
    wasOpenRef.current = open;
  }, [open, initialFilterSelectValue, initialFilterText, pageDateRange, initialSystem, initialShipmentType, clearValidation]);

  useEffect(() => {
    if (open && !invoiceSetting && !orgLoading) {
      dispatch(getInvoiceSettings());
    }
  }, [open, invoiceSetting, orgLoading, dispatch]);

  const buildModalFilterObj = useCallback(
    (options: FilterObj) => {
      setModalSelectedAccountRows([]);
      setExpandedAccounts({});
      const filterDate = options.dateRange || modalDateRange;
      const fromDate = moment(filterDate.startDate).format('DD/MM/YYYY');
      const toDate = moment(filterDate.endDate).format('DD/MM/YYYY');
      const filterSystem = options.system || modalSystem;
      const filterType = options.shipmentType || modalShipmentType;
      const filterSearchKey = modalFilterSelectValue;
      const filterSearchValue = 'searchValue' in options ? options.searchValue : modalFilterText;
      const filter: FilterType = {
        currentPage: options.currentPage || currentPage,
        perPage: options.perPage || perPage,
        fromDate,
        toDate,
        searchKey: filterSearchKey,
        searchValue: filterSearchValue,
        systems: filterSystem,
        type: filterType,
      };

      if (!filterSearchValue) {
        delete filter.searchKey;
        delete filter.searchValue;
      }

      return filter;
    },
    [modalDateRange, modalSystem, modalShipmentType, modalFilterSelectValue, modalFilterText, currentPage, perPage],
  );

  const onModalSearchSelectChange = useCallback((value: string) => {
    setModalFilterSelectValue(value);
  }, []);

  const onModalSearchTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    setModalFilterText(e.target.value);
  }, []);

  const onModalTextSearch = useCallback(() => {
    setHasModifiedModalFilters(true);
    dispatch(getInvoiceList(buildModalFilterObj({ currentPage: 1 })));
  }, [dispatch, buildModalFilterObj]);

  const onModalTextClear = useCallback(() => {
    setModalFilterText('');
    setHasModifiedModalFilters(true);
    dispatch(getInvoiceList(buildModalFilterObj({ searchValue: undefined, currentPage: 1 })));
  }, [dispatch, buildModalFilterObj]);

  const onModalDateChange = useCallback(
    ({ startDate, endDate }: { startDate: Date; endDate: Date }, isOpen: boolean) => {
      if (isOpen) return;
      const nextDateRange = { startDate, endDate };
      setModalDateRange(nextDateRange);
      setHasModifiedModalFilters(true);
      dispatch(getInvoiceList(buildModalFilterObj({ dateRange: nextDateRange, currentPage: 1 })));
    },
    [dispatch, buildModalFilterObj],
  );

  const onModalSystemFilter = useCallback(
    (selected: Array<string>) => {
      setModalSystem(selected);
      setHasModifiedModalFilters(true);
      dispatch(getInvoiceList(buildModalFilterObj({ system: selected, currentPage: 1 })));
    },
    [dispatch, buildModalFilterObj],
  );

  const onModalTypeFilter = useCallback(
    (selected: Array<string>) => {
      setModalShipmentType(selected);
      setHasModifiedModalFilters(true);
      dispatch(getInvoiceList(buildModalFilterObj({ shipmentType: selected, currentPage: 1 })));
    },
    [dispatch, buildModalFilterObj],
  );

  const onModalClear = useCallback(() => {
    setModalSystem([]);
    setModalShipmentType([]);
    setModalFilterText('');
    setModalDateRange(initialDateRange);
    setHasModifiedModalFilters(true);
    dispatch(
      getInvoiceList(
        buildModalFilterObj({
          dateRange: initialDateRange,
          system: [],
          shipmentType: [],
          searchValue: undefined,
          currentPage: 1,
        }),
      ),
    );
  }, [dispatch, buildModalFilterObj]);

  const onModalPageChange = useCallback(
    (_: unknown, page: number) => {
      setHasModifiedModalFilters(true);
      dispatch(getInvoiceList(buildModalFilterObj({ currentPage: page + 1 })));
    },
    [dispatch, buildModalFilterObj],
  );

  const onModalRowsPerPageChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setHasModifiedModalFilters(true);
      dispatch(getInvoiceList(buildModalFilterObj({ perPage: parseInt(event.target.value, 10), currentPage: 1 })));
    },
    [dispatch, buildModalFilterObj],
  );

  const handleCloseModalClean = useCallback(() => {
    if (isSubmitting) return;
    if (hasModifiedModalFilters) {
      onRestorePageFilters();
    }
    onClose();
  }, [isSubmitting, hasModifiedModalFilters, onRestorePageFilters, onClose]);

  useEffect(() => {
    if (!open) return;
    if (modalInvConfig.taxname && modalInvConfig.taxno) {
      const lowerName = modalInvConfig.taxname.toLowerCase();
      if (lowerName.includes('gst')) {
        validate('taxno', 'gst', modalInvConfig.taxno);
      } else if (lowerName.includes('pan')) {
        validate('taxno', 'pan', modalInvConfig.taxno);
      }
    }
  }, [open, modalInvConfig.taxname, modalInvConfig.taxno, validate]);

  const handleApplyTaxnoSuggestion = useCallback((suggestion: string) => {
    setModalInvConfig(prev => ({ ...prev, taxno: suggestion }));
  }, []);

  const sacCodeOptions = useMemo(() => {
    const sacCode = invoiceSetting?.sacCode || [];
    return sacCode.map(s => ({ display: s.display, value: s.key }));
  }, [invoiceSetting?.sacCode]);

  const taxInformationDropdownOptions = useMemo(() => {
    const taxInformation = invoiceSetting?.taxInformation || [];
    return taxInformation.map(s => ({ display: s.display, value: s.key }));
  }, [invoiceSetting?.taxInformation]);

  const toggleExpandAccount = useCallback((groupKey: string) => {
    setExpandedAccounts(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  }, []);

  // Selection is strictly decoupled from expansion: selecting an account never auto-expands its details.
  const handleSelectAccountGroup = useCallback((data: AccountHolderUnbilledData, isChecked: boolean) => {
    const paramObj: SelectedParamType = {
      _userId: data.user?._id,
      accountNumber: data.accountNumber,
      shipmentRouteType: data.shipmentRouteType || '',
      _systemId: data._systemId?._id || '',
      systemName: data._systemId?.name || '',
      shipments: isChecked ? data.shipments || [] : [],
      uniqueKey: data.uniqueKey,
    };
    setModalSelectedAccountRows(prev => updateSelectedAccountRowsList(prev, paramObj));
  }, []);

  const handleSelectAllAccountsInModal = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (event.target.checked) {
        const hasCanGenerateFlag = list.some(item => item?.canGenerateInvoice);
        const eligibleAccounts = hasCanGenerateFlag ? list.filter(item => item?.canGenerateInvoice) : list;
        const nextRows: SelectedParamType[] = eligibleAccounts
          .filter(data => data.shipments?.length)
          .map(data => ({
            _userId: data.user?._id,
            accountNumber: data.accountNumber,
            shipmentRouteType: data.shipmentRouteType || '',
            _systemId: data._systemId?._id || '',
            systemName: data._systemId?.name || '',
            shipments: data.shipments || [],
            uniqueKey: data.uniqueKey,
          }));
        setModalSelectedAccountRows(nextRows);
      } else {
        setModalSelectedAccountRows([]);
      }
    },
    [list],
  );

  const handleToggleSingleShipment = useCallback(
    (data: AccountHolderUnbilledData, shipment: ShipmentObj, isChecked: boolean) => {
      setModalSelectedAccountRows(prev => {
        const existingEntry = prev.find(item => isSameAccountEntry(item, data));
        const currentShipments = existingEntry?.shipments || [];
        const nextShipments = isChecked
          ? currentShipments.some(s => s._id === shipment._id)
            ? currentShipments
            : [...currentShipments, shipment]
          : currentShipments.filter(s => s._id !== shipment._id);

        const paramObj: SelectedParamType = {
          _userId: data.user?._id,
          accountNumber: data.accountNumber,
          shipmentRouteType: data.shipmentRouteType || '',
          _systemId: data._systemId?._id || '',
          systemName: data._systemId?.name || '',
          shipments: nextShipments,
          uniqueKey: data.uniqueKey,
        };
        return updateSelectedAccountRowsList(prev, paramObj);
      });
    },
    [],
  );

  const totalSelectedShipmentsCount = useMemo(
    () => modalSelectedAccountRows.reduce((acc, row) => acc + (row.shipments?.length || 0), 0),
    [modalSelectedAccountRows],
  );

  const totalSelectedAmount = useMemo(
    () => calculateSelectedPricingSum(modalSelectedAccountRows),
    [modalSelectedAccountRows],
  );

  const primaryCurrencySymbol = useMemo(() => {
    const firstSelectedShipment = modalSelectedAccountRows[0]?.shipments?.[0] || list?.[0]?.shipments?.[0];
    return (
      getCurrencySymbolByName(firstSelectedShipment?.sellingPricing?.currency) ||
      getCurrencySymbolByName(firstSelectedShipment?._packageId?.currency) ||
      ''
    );
  }, [modalSelectedAccountRows, list]);

  const handleModalSendTo365 = useCallback(() => {
    const accountData = modalSelectedAccountRows.map((item: SelectedParamType) => ({
      _userId: item?._userId,
      accountNo: item.accountNumber,
      _systemId: item._systemId,
      _shipmentIds: item.shipments.map(s => s._id),
      _shipmentNumbers: item.shipments.map(s => s.shipmentNumber),
    }));
    dispatch(
      sendAccountDataTo365({
        accountData,
        dueDate: dayjs(modalInvConfig.dueDate).format('YYYY-MM-DD'),
        invoiceDate: dayjs(modalInvConfig.invoiceDate).format('YYYY-MM-DD'),
      }),
    );
  }, [modalSelectedAccountRows, dispatch, modalInvConfig.dueDate, modalInvConfig.invoiceDate]);

  const handleModalSubmit = useCallback(() => {
    onSubmitInvoice(modalInvConfig, modalSelectedAccountRows, () => {
      onClose();
    });
  }, [onSubmitInvoice, modalInvConfig, modalSelectedAccountRows, onClose]);

  const missingFields = useMemo(
    () => validateInvoiceConfig(modalInvConfig, modalSelectedAccountRows, isWyngsMy),
    [modalInvConfig, modalSelectedAccountRows, isWyngsMy],
  );

  const isConfigIncomplete = missingFields.length > 0;
  const isBusy = listLoading || orgLoading || loadingD365 || isSubmitting;
  const isCreateDisabled = isBusy || isConfigIncomplete;

  const disabledTooltipContent = useMemo(() => {
    if (isSubmitting) {
      return 'Creating invoice, please wait...';
    }
    if (listLoading || orgLoading || loadingD365) {
      return 'Loading data, please wait...';
    }
    if (missingFields.length > 0) {
      return (
        <Box sx={{ p: 0.5, maxWidth: 300 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, mb: 0.5, color: 'inherit' }}>
            Cannot create invoice. Required field(s) missing:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2, fontSize: '0.75rem', lineHeight: 1.5 }}>
            {missingFields.map(field => (
              <li key={field}>{field}</li>
            ))}
          </Box>
        </Box>
      );
    }
    return null;
  }, [isSubmitting, listLoading, orgLoading, loadingD365, missingFields]);

  return (
    <Modal
      open={open}
      onClose={handleCloseModalClean}
      maxWidth="xl"
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: '1480px',
          height: { xs: '96dvh', sm: '94dvh', md: '92dvh' },
          maxHeight: '95dvh',
          m: { xs: 1, sm: 2, md: 2.5 },
          borderRadius: '18px',
          background: 'transparent',
          boxShadow: 'none',
        },
      }}
      dialogContainerSx={{
        width: '100%',
        height: '100%',
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        p: { xs: 2, sm: 2.5 },
        bgcolor: 'background.paper',
        color: 'text.primary',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: '18px',
        overflow: 'hidden',
      }}
    >
      {/* Top Enterprise Header Bar */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          flexDirection: { xs: 'column', md: 'row' },
          gap: 1.5,
          pb: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ReceiptLongOutlinedIcon />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: '1.15rem', color: 'text.primary', lineHeight: 1.2 }}
            >
              Create Account Invoice
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem', mt: 0.25 }}>
              Select unbilled accounts or shipments on the left and configure invoice parameters on the right
            </Typography>
          </Box>
        </Stack>

        {/* Live KPI Summary Strip & Close Control */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          flexWrap="wrap"
          sx={{ width: { xs: '100%', md: 'auto' }, justifyContent: 'flex-end' }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: 2,
              py: 0.75,
              borderRadius: '10px',
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box>
              <Typography
                sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}
              >
                Accounts
              </Typography>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: 'text.primary' }}>
                {modalSelectedAccountRows.length}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography
                sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}
              >
                Shipments
              </Typography>
              <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: 'text.primary' }}>
                {totalSelectedShipmentsCount}
              </Typography>
            </Box>
            <Divider orientation="vertical" flexItem />
            <Box>
              <Typography
                sx={{ fontSize: '0.68rem', fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}
              >
                Selected Amount
              </Typography>
              <Tooltip
                title={`Total: ${primaryCurrencySymbol} ${formatNumber(totalSelectedAmount, locale)}`}
                arrow
                placement="top"
              >
                <Typography sx={{ fontSize: '0.92rem', fontWeight: 800, color: 'primary.main' }}>
                  {primaryCurrencySymbol} {formatNumber(totalSelectedAmount, locale)}
                </Typography>
              </Tooltip>
            </Box>
          </Box>

          <IconButton
            aria-label="Close modal"
            onClick={handleCloseModalClean}
            disabled={isSubmitting}
            size="small"
            sx={{
              width: 34,
              height: 34,
              color: 'text.secondary',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: '10px',
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Box>

      {/* Main Split Workspace Body */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          py: 1.5,
          overflowY: { xs: 'auto', lg: 'hidden' },
          overflowX: 'hidden',
        }}
      >
        <Grid container sx={{ height: { xs: 'auto', lg: '100%' }, minHeight: 0, m: 0, width: '100%' }}>
          {/* Left Workspace: Unbilled Accounts & Shipments Selection */}
          <Grid
            item
            xs={12}
            lg={7.5}
            sx={{
              height: { xs: 'auto', lg: '100%' },
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              p: '0 !important',
              pr: { lg: '14px !important' },
              mb: { xs: 2.5, lg: 0 },
            }}
          >
            <ShipmentDetailsSection
              list={list}
              listLoading={listLoading}
              totalCount={totalCount}
              currentPage={currentPage}
              perPage={perPage}
              orgName={name}
              activeSystemList={activeSystemList}
              filterSelectValue={modalFilterSelectValue}
              filterText={modalFilterText}
              dateRange={modalDateRange}
              system={modalSystem}
              shipmentType={modalShipmentType}
              selectedAccountRows={modalSelectedAccountRows}
              expandedAccounts={expandedAccounts}
              onSearchSelectChange={onModalSearchSelectChange}
              onSearchTextChange={onModalSearchTextChange}
              onTextSearch={onModalTextSearch}
              onTextClear={onModalTextClear}
              onDateChange={onModalDateChange}
              onSystemFilter={onModalSystemFilter}
              onTypeFilter={onModalTypeFilter}
              onClearFilters={onModalClear}
              onPageChange={onModalPageChange}
              onRowsPerPageChange={onModalRowsPerPageChange}
              onSelectAllAccounts={handleSelectAllAccountsInModal}
              onSelectAccountGroup={handleSelectAccountGroup}
              onToggleSingleShipment={handleToggleSingleShipment}
              onToggleExpandAccount={toggleExpandAccount}
            />
          </Grid>

          {/* Right Workspace: Integrated Invoice Configuration & Live Preview */}
          <Grid
            item
            xs={12}
            lg={4.5}
            sx={{
              height: { xs: 'auto', lg: '100%' },
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              p: '0 !important',
              pl: { lg: '14px !important' },
            }}
          >
            <InvoiceConfigurationSection
              invConfig={modalInvConfig}
              setInvConfig={setModalInvConfig}
              orgName={name}
              isWyngsMy={isWyngsMy}
              sacCodeOptions={sacCodeOptions}
              taxInformationDropdownOptions={taxInformationDropdownOptions}
              selectedAccountRows={modalSelectedAccountRows}
              totalSelectedShipmentsCount={totalSelectedShipmentsCount}
              totalSelectedAmount={totalSelectedAmount}
              primaryCurrencySymbol={primaryCurrencySymbol}
              loadingD365={loadingD365}
              onSendTo365={handleModalSendTo365}
              taxnoValidationState={validationStates['taxno']}
              onApplyTaxnoSuggestion={handleApplyTaxnoSuggestion}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Bottom Sticky Modal Footer Actions */}
      <Box
        sx={{
          pt: 1.5,
          borderTop: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1.5,
          flexShrink: 0,
        }}
      >
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.82rem' }}>
          {modalSelectedAccountRows.length > 0
            ? `${
                modalSelectedAccountRows.length
              } invoice(s) queued across ${totalSelectedShipmentsCount} shipment(s) • Total: ${primaryCurrencySymbol} ${formatNumber(
                totalSelectedAmount,
                locale,
              )}`
            : 'Select at least one account or shipment and set Invoice Date to create invoice(s).'}
        </Typography>

        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
          <SbButton
            variant="outlined"
            className="rounded-full"
            sx={{ minWidth: 115, height: '40px !important' }}
            onClick={handleCloseModalClean}
            disabled={isSubmitting}
          >
            Cancel
          </SbButton>
          <Tooltip
            title={disabledTooltipContent || ''}
            arrow
            enterDelay={150}
            placement="top"
            disableHoverListener={!isCreateDisabled}
          >
            <span style={{ display: 'inline-flex' }}>
              <SbButton
                variant="contained"
                className="rounded-full"
                sx={{ minWidth: 160, height: '40px !important' }}
                onClick={handleModalSubmit}
                disabled={isCreateDisabled}
                loading={isSubmitting}
              >
                Create Invoice
              </SbButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>
    </Modal>
  );
});

export default CreateInvoiceModal;
